/**
 * Reads the sheets of an `.xlsx` for the in-page file preview.
 *
 * The parser is `read-excel-file` (maintained, on the npm registry). It turns
 * every cell address it meets into a dense row/column array, so a hostile file
 * with one cell at `XFD1048576` makes it allocate until the tab dies, and that
 * cannot be caught. `assertSpreadsheetWithinBounds` therefore reads the same
 * XML entries first and refuses anything that would blow up, and the failure
 * is an ordinary rejected promise the preview shows as "could not be shown
 * here, download it".
 *
 * Legacy binary `.xls` is not read here (no registry parser handles it); the
 * preview never gets that far, see `isLegacyExcelFile` in `FilePreview`.
 */

/** What one cell of a previewed sheet holds; `null` is an empty cell. */
export type SpreadsheetCell = string | number | boolean | Date | null;

export interface SpreadsheetSheet {
  name: string;
  rows: SpreadsheetCell[][];
}

/**
 * Bounds past which a spreadsheet is not parsed in the page (the user is sent
 * to the download instead). The preview then draws at most the first
 * `PREVIEW_ROW_LIMIT` rows and `PREVIEW_COLUMN_LIMIT` columns of a sheet.
 */
export const SPREADSHEET_LIMITS = {
  /** The file as stored (compressed). */
  fileBytes: 10 * 1024 * 1024,
  /** All XML parts together, once unzipped (a zip bomb stays under this). */
  inflatedBytes: 40 * 1024 * 1024,
  /** Highest row number times highest column number across every sheet. */
  cells: 4_000_000,
} as const;

export const PREVIEW_ROW_LIMIT = 1000;
export const PREVIEW_COLUMN_LIMIT = 100;

/** Excel's own ceiling: row 1,048,576 and column XFD (16,384). */
const MAX_ROW = 1_048_576;
const MAX_COLUMN = 16_384;

/** The error for a file past the bounds above, or one that is not a sane sheet. */
export class SpreadsheetBoundsError extends Error {
  constructor(reason: string) {
    super(`Spreadsheet not previewed: ${reason}`);
    this.name = 'SpreadsheetBoundsError';
  }
}

// Every `r="A1"` (cell) or `r="7"` (row) attribute, whatever the tag, its
// namespace prefix or its quote style, so a sheet hidden in an odd part of the
// package is covered too. Linear: a literal prefix then a bounded group. Up to
// 12 letters or underscores are captured so an absurd column like `__proto__1`
// is seen and rejected instead of being skipped.
const REFERENCE = /\br=["']([A-Za-z_]{0,12})(\d{1,12})["']/g;

const columnNumber = (letters: string) => {
  let column = 0;
  for (const char of letters.toUpperCase()) {
    column = column * 26 + (char.charCodeAt(0) - 64);
  }
  return column;
};

/**
 * Throws `SpreadsheetBoundsError` unless the package is small enough to parse
 * and no cell address in it runs past Excel's own grid or past the cell budget.
 * Only the declared sizes of the XML parts ever size a buffer, and a part
 * past the budget is never inflated.
 */
export const assertSpreadsheetWithinBounds = async (bytes: Uint8Array) => {
  if (bytes.byteLength > SPREADSHEET_LIMITS.fileBytes) {
    throw new SpreadsheetBoundsError('file too large');
  }

  const { unzipSync } = await import('fflate');
  let inflated = 0;
  let oversized = false;
  // The same parts the parser reads (`.xml` and `.xml.rels`), nothing else.
  const parts = unzipSync(bytes, {
    filter: ({ name, originalSize }) => {
      if (!name.endsWith('.xml') && !name.endsWith('.xml.rels')) return false;
      inflated += originalSize;
      if (inflated > SPREADSHEET_LIMITS.inflatedBytes) oversized = true;
      return !oversized;
    },
  });
  if (oversized) throw new SpreadsheetBoundsError('contents too large');

  const decoder = new TextDecoder();
  let maxRow = 1;
  let maxColumn = 1;
  for (const part of Object.values(parts)) {
    for (const [, letters, digits] of decoder
      .decode(part)
      .matchAll(REFERENCE)) {
      const row = Number(digits);
      const column = columnNumber(letters);
      if (letters.length > 3 || column > MAX_COLUMN || row > MAX_ROW) {
        throw new SpreadsheetBoundsError('cell outside the sheet grid');
      }
      if (row > maxRow) maxRow = row;
      if (column > maxColumn) maxColumn = column;
    }
  }
  if (maxRow * maxColumn > SPREADSHEET_LIMITS.cells) {
    throw new SpreadsheetBoundsError('sheet too large');
  }
};

/**
 * Every sheet of an `.xlsx`, in workbook order, as rows of cell values.
 * Sheets are an ordered list, never an object keyed by sheet name: the name is
 * file content, and `__proto__` is a legal one.
 */
export const readSpreadsheet = async (
  data: ArrayBuffer
): Promise<SpreadsheetSheet[]> => {
  await assertSpreadsheetWithinBounds(new Uint8Array(data));
  const { default: readXlsxFile } = await import('read-excel-file/universal');
  const sheets = await readXlsxFile(data);
  // The library's typings declare a date cell as `typeof Date` (the class); the
  // value is a `Date` instance, which the preview tests read back.
  return sheets.map(({ sheet, data: rows }) => ({
    name: sheet,
    rows: rows as unknown as SpreadsheetCell[][],
  }));
};

const pad = (value: number, length = 2) => String(value).padStart(length, '0');

/**
 * A cell as the preview prints it. A date cell has no time zone in the file,
 * so it is read and written as UTC: a date-only cell prints `1996-01-01`, one
 * with a time prints `1996-01-01 13:30`.
 */
export const formatSpreadsheetCell = (cell: SpreadsheetCell) => {
  if (cell === null) return '';
  if (cell instanceof Date) {
    if (Number.isNaN(cell.getTime())) return '';
    const day = `${pad(cell.getUTCFullYear(), 4)}-${pad(
      cell.getUTCMonth() + 1
    )}-${pad(cell.getUTCDate())}`;
    const hours = cell.getUTCHours();
    const minutes = cell.getUTCMinutes();
    const seconds = cell.getUTCSeconds();
    if (!hours && !minutes && !seconds) return day;
    return `${day} ${pad(hours)}:${pad(minutes)}${
      seconds ? `:${pad(seconds)}` : ''
    }`;
  }
  return String(cell);
};
